import { useEffect, useMemo, memo } from 'react'
import {
    Grid,
    Checkbox,
    Button,
    Popover,
    TableRow,
    TableCell,
    IconButton
} from '@mui/material'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faArrowUp, faEye, faUser, faFilePdf, faPen } from '@fortawesome/free-solid-svg-icons'
import { dateV2 } from '../../utils/DateUtils'
import { historyUsers, usersFilteredFromMap } from '../../utils/userUtils'
import { segundosHoras } from '../../utils/NumberUtils'
import { pdfMakeRoutes, reportsRoutes } from '../../routes'

const ReportTableRow = memo(({
    item,
    index,
    columnasParaMostrar,
    admin,
    isOperator,
    isOperatorInspector,
    isUniversalInspector,
    isUniversalChief,
    isSapExecutive,
    isShiftManager,
    isChiefMachinery,
    typeReportsSelected,
    selectItem,
    openModal,
    openReviewModal,
    elevar,
    execute,
    usersMap,
    tipo,
    handleSiteClick,
    handleSiteClose,
    siteAnchorEl,
    isSitePopoverOpen,
    sitePopoverId,
    siteNameDescription,
    onContextMenu,
    canSelect,
    isSearching
}) => {
    const valuesAssignatiosn = [
        'usersAssigned',
        'chiefMachineryApprovedBy',
        'sapExecutiveApprovedBy',
        'shiftManagerApprovedBy'
    ]

    const isAssignEnabled = useMemo(() => {
        const currentState = item.state || item.estado
        const currentLevel = item.level || 0
        const allowedStates = ['Asignar', 'Reasignar', 'En proceso']
        return allowedStates.includes(currentState) && currentLevel === 0
    }, [item.state, item.estado, item.level])

    const canAction = useMemo(() => {
        const isSpecialOperator = isUniversalInspector || isUniversalChief || isOperatorInspector
        return (admin || isSapExecutive || isShiftManager || isChiefMachinery || isSpecialOperator) && (!isOperator || isSpecialOperator)
    }, [admin, isSapExecutive, isShiftManager, isChiefMachinery, isOperator, isUniversalInspector, isUniversalChief, isOperatorInspector])

    const usersToDisplay = useMemo(() => {
        const estado = item.estado || item.state
        const currentExecutionStates = ['En proceso', 'Ejecutando por operador']

        if (currentExecutionStates.includes(estado)) {
            return usersFilteredFromMap(item.usersAssigned, usersMap)
        }

        return historyUsers(item.historialAsignaciones)
    }, [item.estado, item.state, item.usersAssigned, item.historialAsignaciones, usersMap])

    const handleViewPdf = async (reportItem) => {
        if (reportItem && reportItem.urlPdf) {
            try {
                const response = await reportsRoutes.getPdfUrl(reportItem._id)
                if (response.data && response.data.url) {
                    window.open(response.data.url, '_blank')
                } else {
                    alert('No se pudo obtener la URL del PDF. Por favor, intente regenerarlo.')
                }
            } catch (error) {
                console.error('Error fetching PDF URL:', error)
                alert('El enlace del PDF ha expirado o no se pudo renovar. Intente regenerar el PDF desde el detalle.')
            }
        } else {
            alert('El PDF no está disponible. Genérelo desde el detalle del reporte.')
        }
    }

    const customBodyCellStyle = {
        padding: '12px 8px',
        textAlign: 'center',
        whiteSpace: 'normal',
        wordBreak: 'break-word',
        lineHeight: '1.4',
        fontSize: '14px'
    }

    const renderCustomColumns = () => (
        <>
            <TableCell
                align="center"
                sx={{ background: '#fff', padding: '0px 10px', width: 60 }}
            >
                {canSelect && (
                    <Checkbox
                        onChange={(e) => {
                            selectItem(e, index)
                        }}
                        checked={item.checked}
                    />
                )}
            </TableCell>
            {columnasParaMostrar.map((col, i) => {
                const isUserType = valuesAssignatiosn.includes(col.value)
                let cellValue = item[col.value]

                // Custom logic to match Default View
                if (col.value === 'idIndex') {
                    cellValue = item.newIdIndex || item.idIndex || item.index || 'S/I'
                } else if (col.value === 'machine') {
                    cellValue = item.maquina?.equ || item.machine || 'S/I'
                } else if (col.value === 'model') {
                    cellValue = item.maquina?.model || item.model || 'S/I'
                } else if (col.value === 'guide') {
                    cellValue = (item.guide === 'Pauta de Inspección') ? 'P.I.' : item.guide
                } else if (col.value === 'hallazgosCount') {
                    cellValue = (item.hallazgosCount > 0) ? item.hallazgosCount : 'N/A'
                } else if (col.value === 'noChecksCount') {
                    cellValue = (item.noChecksCount > 0) ? item.noChecksCount : 'N/A'
                } else if (col.value === 'hourMeter' && item.maquina) {
                    cellValue = item.maquina.hourMeter
                } else if (col.value === 'sapId') {
                    cellValue = item.sapId || 'S/I'
                } else if (col.value === 'progress') {
                    cellValue = item.progress ? `${Math.round(item.progress)}%` : '0%'
                } else if (col.value === 'assignedBy') {
                    cellValue = item.historialAsignaciones?.[0]?.asignadoPor
                        ? (usersMap[item.historialAsignaciones[0].asignadoPor]
                            ? `${usersMap[item.historialAsignaciones[0].asignadoPor].name} ${usersMap[item.historialAsignaciones[0].asignadoPor].lastName}`
                            : (item.historialAsignaciones[0].asignadoPor.name
                                ? `${item.historialAsignaciones[0].asignadoPor.name} ${item.historialAsignaciones[0].asignadoPor.lastName}`
                                : '- Desconocido'))
                        : '- Sin asignaciones'
                }

                return (
                    <TableCell key={i} align="center" sx={{ ...customBodyCellStyle, width: col.width }}>
                        {col.value === 'site'
                            ? (
                                <>
                                    <Button
                                        aria-describedby={sitePopoverId}
                                        size="small"
                                        style={{ fontSize: '12px', minWidth: 'auto', padding: '2px' }}
                                        onClick={(e) => {
                                            handleSiteClick(e, item)
                                        }}
                                    >
                                        {item.obra?.idObra || item.site}
                                    </Button>
                                    <Popover
                                        id={sitePopoverId}
                                        open={isSitePopoverOpen}
                                        anchorEl={siteAnchorEl}
                                        onClose={handleSiteClose}
                                        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                                        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
                                    >
                                        <div style={{ padding: 5 }}>
                                            <p>{siteNameDescription}</p>
                                        </div>
                                    </Popover>
                                </>
                            )
                            : (col.value === 'urlPdf' && cellValue) || (typeof cellValue === 'string' && cellValue.includes('http'))
                                ? (
                                    <span
                                        onClick={() => handleViewPdf(item)}
                                        style={{ color: '#1976d2', cursor: 'pointer', textDecoration: 'underline', fontWeight: 'bold' }}
                                    >
                                        Link
                                    </span>
                                )
                                : isUserType
                                    ? (
                                        <div style={{ whiteSpace: 'pre-line' }}>
                                            {col.value === 'usersAssigned'
                                                ? usersToDisplay.join('\n')
                                                : usersFilteredFromMap(item[col.value], usersMap).join('\n')}
                                        </div>
                                    )
                                    : col.value === 'urlPdf' ? (
                                        cellValue || (typeof cellValue === 'string' && cellValue.includes('http'))
                                            ? (
                                                <span
                                                    onClick={() => handleViewPdf(item)}
                                                    style={{ color: '#1976d2', cursor: 'pointer', textDecoration: 'underline', fontWeight: 'bold' }}
                                                >
                                                        link
                                                </span>
                                            )
                                            : 'Sin URL'
                                    )
                                        : col.isDate
                                            ? (
                                                dateV2(cellValue, 'dd/mm/yyyy', col.value !== 'datePrev')
                                            )
                                            : col.value === 'hourMeter'
                                                ? (
                                                    segundosHoras(cellValue) + ' hrs'
                                                )
                                                : col.value === 'estado'
                                                    ? (
                                                        <strong>{cellValue}</strong>
                                                    )
                                                    : (
                                                        cellValue
                                                    )}
                    </TableCell>
                )
            })}
            <TableCell
                align="center"
                sx={{ background: '#fff', padding: '4px', width: 70 }}
            >
                <Grid container spacing={1} justifyContent="center">
                    <Grid item>
                        {(item.origen && canAction)
                            ? (
                                <IconButton
                                    size="small"
                                    title={`Elevar OT ${item.newIdIndex || item.index}`}
                                    onClick={() => elevar(item)}
                                >
                                    <FontAwesomeIcon icon={faArrowUp} />
                                </IconButton>
                            )
                            : (
                                <IconButton
                                    size="small"
                                    title={`Ver OT ${item.newIdIndex || item.index}`}
                                    onClick={() => openReviewModal(item)}
                                >
                                    <FontAwesomeIcon icon={faEye} />
                                </IconButton>
                            )}
                    </Grid>

                    {item.enabled && !item.origen && canAction && !isUniversalInspector && (
                        <Grid item>
                            <IconButton
                                size="small"
                                disabled={!isAssignEnabled}
                                title={`Asignar OT ${item.newIdIndex || item.index} a operario.`}
                                onClick={() => openModal(item)}
                            >
                                <FontAwesomeIcon icon={faUser} />
                            </IconButton>
                        </Grid>
                    )}
                </Grid>
            </TableCell>
        </>
    )

    // Render default Columns (Aligned with ReportTableHeader)
    const renderDefaultColumns = () => (
        <>
            <TableCell align="center" sx={{ background: '#fff' }}>
                {canSelect && (
                    <Checkbox
                        onChange={(e) => {
                            selectItem(e, index)
                        }}
                        checked={item.checked}
                    />
                )}
            </TableCell>

            <TableCell align="center">{item.newIdIndex || item.idIndex || item.index || 'S/I'}</TableCell>
            <TableCell align="center">{item.maquina?.equ || item.machine || 'S/I'}</TableCell>

            <TableCell align="center">
                {/* PT2 mostrar info del reporte con fallback a la maquina */}
                {(item.hourMeter !== null && item.hourMeter !== undefined && item.hourMeter > 0)
                    ? segundosHoras(item.hourMeter) + ' hrs'
                    : (item.maquina?.hourMeter)
                        ? segundosHoras(item.maquina.hourMeter) + ' hrs'
                        : 'S/I'}
            </TableCell>

            <TableCell align="center">
                {item.guide === 'Pauta de Inspección' ? 'P.I.' : item.guide}
            </TableCell>

            <TableCell align="center">{item.idPm || 'S/I'}</TableCell>
            <TableCell align="center" sx={{ minWidth: 80 }}>{item.maquina?.model || item.model || 'S/I'}</TableCell>

            <TableCell align="center">{item.progress ? `${Math.round(item.progress)}%` : '0%'}</TableCell>

            <TableCell align="center">{dateV2(item.datePrev, 'dd/mm/yyyy', false)}</TableCell>

            {/* Fechas Inicio/Término */}
            {(isSearching || ['En proceso', 'Por cerrar', 'Completadas', 'Ejecutando por operador', 'Reasignar', 'Revisión Supervisor', 'Revisión J. Maquinaria', 'Revisión SAP', 'Asignar', 'Origen', 'all', 'Todos'].includes(typeReportsSelected)) && (
                <>
                    <TableCell align="center">{dateV2(item.dateInit, 'dd/mm/yyyy')}</TableCell>
                    <TableCell align="center">{dateV2(item.endReport, 'dd/mm/yyyy')}</TableCell>
                </>
            )}

            <TableCell align="center">
                <strong>{item.estado}</strong>
            </TableCell>

            {(isSearching || ['En proceso', 'Por cerrar', 'Completadas', 'Ejecutando por operador', 'Reasignar', 'Revisión Supervisor', 'Revisión J. Maquinaria', 'Revisión SAP', 'Asignar', 'Origen', 'all', 'Todos'].includes(typeReportsSelected)) && (
                <>
                    <TableCell align="center">
                        {item.hallazgosCount > 0 ? item.hallazgosCount : 'N/A'}
                    </TableCell>
                    <TableCell align="center">
                        {item.noChecksCount > 0 ? item.noChecksCount : 'N/A'}
                    </TableCell>
                </>
            )}

            <TableCell align="center">
                {item.sapId || 'S/I'}
            </TableCell>

            {/* Asignado a */}
            {(isSearching || (['En proceso', 'Por cerrar', 'Completadas', 'Ejecutando por operador', 'Reasignar', 'Revisión Supervisor', 'Revisión J. Maquinaria', 'Revisión SAP', 'all', 'Todos'].includes(typeReportsSelected) && typeReportsSelected !== 'Asignar' && typeReportsSelected !== 'Origen')) && (
                <TableCell align="center">
                    <div style={{ whiteSpace: 'pre-line' }}>
                        {usersToDisplay.join('\n')}
                    </div>
                </TableCell>
            )}

            {/* Asignado por */}
            {(isSearching || (['En proceso', 'Por cerrar', 'Completadas', 'Ejecutando por operador', 'Reasignar', 'Revisión Supervisor', 'Revisión J. Maquinaria', 'Revisión SAP', 'all', 'Todos'].includes(typeReportsSelected) && typeReportsSelected !== 'Asignar' && typeReportsSelected !== 'Origen')) && (
                <TableCell align="center">
                    {item.historialAsignaciones?.[0]?.asignadoPor
                        ? (usersMap[item.historialAsignaciones[0].asignadoPor]
                            ? `${usersMap[item.historialAsignaciones[0].asignadoPor].name} ${usersMap[item.historialAsignaciones[0].asignadoPor].lastName}`
                            : (item.historialAsignaciones[0].asignadoPor.name
                                ? `${item.historialAsignaciones[0].asignadoPor.name} ${item.historialAsignaciones[0].asignadoPor.lastName}`
                                : '- Desconocido'))
                        : '- Sin asignaciones'}
                </TableCell>
            )}

            {(isSearching || typeReportsSelected === 'Por cerrar' || typeReportsSelected === 'Completadas') && (
                <TableCell align="center">
                    {usersFilteredFromMap(item.shiftManagerApprovedBy, usersMap)[0]}
                </TableCell>
            )}

            {(isSearching || typeReportsSelected === 'Por cerrar' || typeReportsSelected === 'Completadas') && (
                <TableCell align="center">
                    {usersFilteredFromMap(item.chiefMachineryApprovedBy, usersMap)[0]}
                </TableCell>
            )}

            {(isSearching || typeReportsSelected === 'Completadas') && (
                <TableCell align="center">
                    {usersFilteredFromMap(item.sapExecutiveApprovedBy, usersMap)[0]}
                </TableCell>
            )}

            {(!isOperator || isUniversalChief || isUniversalInspector || isOperatorInspector) && (
                <TableCell align="center">
                    <Button
                        aria-describedby={sitePopoverId}
                        size="small"
                        onClick={(e) => {
                            handleSiteClick(e, item)
                        }}
                    >
                        {item.obra?.idObra || item.site}
                    </Button>
                    <Popover
                        id={sitePopoverId}
                        open={isSitePopoverOpen}
                        anchorEl={siteAnchorEl}
                        onClose={handleSiteClose}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
                    >
                        <div style={{ padding: 5 }}>
                            <p>{siteNameDescription}</p>
                        </div>
                    </Popover>
                </TableCell>
            )}

            {(isSearching || typeReportsSelected === 'Completadas') && (
                <TableCell align="center">
                    {item.urlPdf
                        ? (
                            <span
                                onClick={() => handleViewPdf(item)}
                                style={{ color: '#1976d2', cursor: 'pointer', textDecoration: 'underline', fontWeight: 'bold' }}
                            >
                                link
                            </span>
                        )
                        : (
                            'Sin URL'
                        )}
                </TableCell>
            )}

            <TableCell align="center" sx={{ background: '#fff', padding: '4px', width: 70 }}>
                <Grid container spacing={1} justifyContent="center">
                    <Grid item>
                        {(item.origen && canAction)
                            ? (
                                <IconButton
                                    size="small"
                                    title={`Elevar OT ${item.newIdIndex || item.index}`}
                                    onClick={() => elevar(item)}
                                >
                                    <FontAwesomeIcon icon={faArrowUp} />
                                </IconButton>
                            )
                            : (
                                <IconButton
                                    size="small"
                                    title={`Ver OT ${item.newIdIndex || item.index}`}
                                    onClick={() => openReviewModal(item)}
                                >
                                    <FontAwesomeIcon icon={faEye} />
                                </IconButton>
                            )}
                    </Grid>

                    {item.enabled && !item.origen && canAction && !isUniversalInspector && (
                        <Grid item>
                            <IconButton
                                size="small"
                                disabled={!isAssignEnabled}
                                title={`Asignar OT ${item.newIdIndex || item.index} a operario.`}
                                onClick={() => openModal(item)}
                            >
                                <FontAwesomeIcon icon={faUser} />
                            </IconButton>
                        </Grid>
                    )}
                </Grid>
            </TableCell>
        </>
    )

    return (
        <TableRow
            hover
            role="checkbox"
            tabIndex={-1}
            key={item._id || item.idIndex || index}
            onContextMenu={(event) => onContextMenu(event, item)}
            sx={{ cursor: 'context-menu' }}
        >
            {(columnasParaMostrar && columnasParaMostrar.length > 0) ? renderCustomColumns() : renderDefaultColumns()}
        </TableRow>
    )
})

ReportTableRow.displayName = 'ReportTableRow'

export default ReportTableRow
